import React from 'react'

import get from 'lodash/get'
import { CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js'

import {
  Icon,
  LoadingSpinner,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useAccount, useState } from 'src/hooks'
import { Token } from '@stripe/stripe-js'
import { Button } from 'src/design_system/components/Button/Button'

interface Props {
  onSuccess: (token: Token) => void
}

const CreditCardCvcForm: React.FC<Props> = props => {
  const { onSuccess } = props

  const [isLoading, setIsLoading] = useState(false)
  const [isCvcComplete, setIsCvcComplete] = useState(false)
  const [error, setError] = useState<string>()

  const account = useAccount()
  const last4 = account.stripeSource?.last4

  const stripe = useStripe()
  const elements = useElements()

  const onSubmit = async (formEvent: React.FormEvent<HTMLFormElement>) => {
    if (formEvent) {
      formEvent.preventDefault()
    }

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)

    const cardCVCElement = elements.getElement(CardCvcElement)

    if (cardCVCElement) {
      const { error, token } = await stripe.createToken(
        'cvc_update',
        cardCVCElement,
      )

      if (error) {
        setIsLoading(false)
        const message = get(
          error,
          'message',
          'There has been an error checking your CVC.',
        )

        setError(message)

        return
      }
      if (token) {
        return onSuccess(token)
      }
    }
  }

  const stripeElementProps = {
    style: {
      base: {
        fontFamily: "'Montserrat',sans-serif",
        fontWeight: '500',
        color: '#6f8394',
        lineHeight: '24px',
        '::placeholder': {
          color: '#6f839499',
        },
      },
    },
  }

  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      <form onSubmit={onSubmit}>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            flexDirection: 'column',
            alignItems: 'center',

            filter: isLoading ? 'blur(6px)' : undefined,
          }}
          // Workaround as support for inert was only added in React 19
          {...{ inert: isLoading ? '' : undefined }}
        >
          <Text
            type="caption"
            content={`To complete this purchase, please confirm the CVC code for your payment method ending in ${last4}.`}
            outset={{ left: 'x1' }}
            color="primaryHeading"
            weight="semiBold"
          />
          <div
            style={{
              borderRadius: '12px',
              backgroundColor: 'background',
              width: '7rem',
              maxHeight: '3rem',
              padding: '0.8rem 1rem',

              display: 'grid',
              gridTemplateColumns: '24px auto',
              columnGap: '8px',
            }}
          >
            <Icon name="lock" size="small" primaryColor="primaryBodyText" />
            <CardCvcElement
              onChange={e => {
                setIsCvcComplete(e.complete)
              }}
              options={{
                style: stripeElementProps.style,
                placeholder: 'CVC',
              }}
            />
          </div>
          <div>
            <Button isSubmit isDisabled={!isCvcComplete} title="Submit" />
          </div>
          {error && (
            <Text type="body" color="danger" weight="semiBold">
              {error}
            </Text>
          )}
        </div>
      </form>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: '0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <LoadingSpinner size="medium" />
          <Spacer space="x1" />
          <Text type="body" alignment="center">
            Checking CVC...
          </Text>
        </div>
      )}
    </div>
  )
}

export default CreditCardCvcForm
